// ============================================================
// Chess Arena — Save Manager (localStorage persistence)
// ============================================================

import { PlayerStateData } from '../core/types';

const SAVE_KEY = 'chess_arena_save';
const CURRENT_SAVE_VERSION = 3;

/**
 * Migration functions keyed by the version they migrate FROM.
 * Each returns the updated data object.
 */
const migrations: Record<number, (data: Record<string, unknown>) => Record<string, unknown>> = {
  1: (data) => {
    data.abilityShards = data.abilityShards ?? {};
    data.abilityCharges = data.abilityCharges ?? {};
    data.equippedAbilities = data.equippedAbilities ?? [];
    data.saveVersion = 2;
    return data;
  },
  2: (data) => {
    // Players who already completed onboarding should skip the main menu tutorial
    data.mainMenuTutorialDone = data.onboardingComplete ?? false;
    data.saveVersion = 3;
    return data;
  },
};

class SaveManager {
  /**
   * Serialize and store player state to localStorage.
   */
  save(state: PlayerStateData): void {
    try {
      const serialized = JSON.stringify(state);
      localStorage.setItem(SAVE_KEY, serialized);
    } catch (e) {
      console.error('[SaveManager] Failed to save:', e);
    }
  }

  /**
   * Deserialize player state from localStorage.
   * Returns null if missing, corrupt, or unrecoverable.
   */
  load(): PlayerStateData | null {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return null;

      let data: Record<string, unknown>;
      try {
        data = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        console.warn('[SaveManager] Corrupt save data, clearing.');
        this.clear();
        return null;
      }

      // Validate basic structure
      if (typeof data !== 'object' || data === null) {
        console.warn('[SaveManager] Invalid save structure, clearing.');
        this.clear();
        return null;
      }

      // Apply version migrations
      let version = typeof data.saveVersion === 'number' ? data.saveVersion : 0;
      while (version < CURRENT_SAVE_VERSION) {
        const migrateFn = migrations[version];
        if (migrateFn) {
          data = migrateFn(data);
          version = typeof data.saveVersion === 'number' ? data.saveVersion : version + 1;
        } else {
          // No migration path — can't recover
          console.warn(`[SaveManager] No migration from version ${version}, clearing.`);
          this.clear();
          return null;
        }
      }

      return data as unknown as PlayerStateData;
    } catch (e) {
      console.error('[SaveManager] Failed to load:', e);
      return null;
    }
  }

  /**
   * Wipe all save data.
   */
  clear(): void {
    try {
      localStorage.removeItem(SAVE_KEY);
    } catch (e) {
      console.error('[SaveManager] Failed to clear:', e);
    }
  }
}

const saveManager = new SaveManager();
export default saveManager;
